import React, { useState, useEffect } from 'react';
import { PLANS } from '../config/pricing';

const BillingPanel = ({ onNavigate, user }) => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [billingHistory, setBillingHistory] = useState([]);
  const [billingLoading, setBillingLoading] = useState(true);
  const [portalLoading, setPortalLoading] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem('auth_token');
        const r = await fetch('/api/user/analytics', {
          headers: { 'Authorization': 'Bearer ' + (token || '') }
        });
        if (r.ok) {
          const data = await r.json();
          setAnalytics(data);
        }
      } catch(e) { console.error('Billing fetch error:', e); }
      setLoading(false);
    };
    fetchData();
  }, []);

  // Fetch billing history from Stripe
  useEffect(() => {
    const fetchBilling = async () => {
      try {
        const token = localStorage.getItem('auth_token');
        const r = await fetch('/api/payments/billing-history', {
          headers: { 'Authorization': 'Bearer ' + (token || '') }
        });
        if (r.ok) {
          const data = await r.json();
          setBillingHistory(data.invoices || []);
        }
      } catch(e) { console.error('Billing history error:', e); }
      setBillingLoading(false);
    };
    fetchBilling();
  }, []);

  const handleManageSubscription = async () => {
    setPortalLoading(true);
    try {
      const token = localStorage.getItem('auth_token');
      const r = await fetch('/api/payments/customer-portal', {
        method: 'POST',
        headers: { 'Authorization': 'Bearer ' + (token || ''), 'Content-Type': 'application/json' }
      });
      const data = await r.json();
      if (data.url) {
        window.open(data.url, '_blank');
      } else {
        alert(data.error || 'Failed to open portal');
      }
    } catch(e) {
      alert('Failed to connect to Stripe. Please try again.');
    }
    setPortalLoading(false);
  };

  const planMap = {
    free: { ...PLANS.free, features: ['720p output', 'Basic clips', 'Watermarked'] },
    starter: { ...PLANS.free, features: ['720p output', 'Basic clips', 'Watermarked'] },
    pro: { ...PLANS.pro, features: ['1080p output', 'No watermark', 'Priority processing', 'Custom thumbnails'] },
    business: { ...PLANS.business, features: ['1080p HD output', 'Custom branding', 'API access', 'Priority support', 'Team features'] }
  };

  const userPlan = analytics?.plan || user?.plan || 'free';
  const plan = planMap[userPlan] || planMap.free;
  const usage = analytics?.monthlyUsage || 0;
  const limit = plan.videos || 1;
  const remaining = Math.max(0, limit - usage);
  const usagePercent = Math.min((usage / limit) * 100, 100);

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const getStatusBadge = (status) => {
    const styles = {
      paid: { bg: 'rgba(16,185,129,0.15)', border: 'rgba(16,185,129,0.3)', color: '#10b981', label: 'Paid' },
      open: { bg: 'rgba(251,191,36,0.15)', border: 'rgba(251,191,36,0.3)', color: '#fbbf24', label: 'Pending' },
      void: { bg: 'rgba(239,68,68,0.15)', border: 'rgba(239,68,68,0.3)', color: '#ef4444', label: 'Void' },
      draft: { bg: 'rgba(148,163,184,0.15)', border: 'rgba(148,163,184,0.3)', color: '#94a3b8', label: 'Draft' },
      uncollectible: { bg: 'rgba(239,68,68,0.15)', border: 'rgba(239,68,68,0.3)', color: '#ef4444', label: 'Failed' }
    };
    const s = styles[status] || styles.draft;
    return (
      <span style={{padding:'4px 10px',borderRadius:'6px',background:s.bg,border:'1px solid '+s.border,color:s.color,fontSize:'11px',fontWeight:'600',textTransform:'uppercase'}}>
        {s.label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="user-dashboard">
        <div className="dashboard-loading"><div className="loading-spinner"></div><p>Loading billing data...</p></div>
      </div>
    );
  }

  return (
    <div className="user-dashboard">

      <div className="dashboard-header">
        <div className="user-info">
          <h1>💳 Billing & Subscription</h1>
          <p className="user-email">Manage your plan, usage, and billing</p>
        </div>
      </div>

      {/* Current Plan Card */}
      <div className="dashboard-card" style={{marginBottom:'24px'}}>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',flexWrap:'wrap',gap:'16px'}}>
          <div>
            <h2 style={{color:'#e2e8f0',margin:'0 0 8px',fontSize:'22px'}}>📦 Current Plan</h2>
            <div style={{display:'flex',alignItems:'baseline',gap:'8px',marginBottom:'12px'}}>
              <span style={{fontSize:'32px',fontWeight:'700',background:'linear-gradient(135deg,#8b5cf6,#06b6d4)',WebkitBackgroundClip:'text',WebkitTextFillColor:'transparent'}}>
                {plan.price.monthly === 0 ? 'Free' : '£' + plan.price.monthly.toFixed(2)}
              </span>
              {plan.price.monthly > 0 && <span style={{color:'#94a3b8',fontSize:'14px'}}>/month</span>}
            </div>
            <div style={{display:'inline-block',padding:'6px 16px',borderRadius:'20px',background:'linear-gradient(135deg,rgba(139,92,246,0.2),rgba(6,182,212,0.2))',border:'1px solid rgba(139,92,246,0.3)',marginBottom:'12px'}}>
              <span style={{color:'#a78bfa',fontWeight:'600',fontSize:'14px'}}>{plan.name}</span>
            </div>
            <p style={{color:'#94a3b8',fontSize:'14px',margin:'8px 0 0'}}>{limit} video{limit !== 1 ? 's' : ''}/month · {plan.clips || limit * 3} clips</p>
            {analytics?.memberSince && <p style={{color:'#64748b',fontSize:'12px',margin:'4px 0 0'}}>Member since {new Date(analytics.memberSince).toLocaleDateString('en-GB', {day:'numeric',month:'short',year:'numeric'})}</p>}
          </div>
          {userPlan !== 'business' && (
            <button onClick={() => onNavigate && onNavigate('upgrade')}
              style={{padding:'14px 28px',borderRadius:'12px',border:'none',background:'linear-gradient(135deg,#8b5cf6,#7c3aed)',color:'white',fontWeight:'700',fontSize:'15px',cursor:'pointer',transition:'all 0.3s'}}>
              ⬆️ Upgrade Plan
            </button>
          )}
        </div>
      </div>

      {/* Plan Features */}
      <div className="dashboard-card" style={{marginBottom:'24px'}}>
        <h2 style={{color:'#e2e8f0',margin:'0 0 16px',fontSize:'18px'}}>✨ Plan Features</h2>
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(200px,1fr))',gap:'12px'}}>
          {plan.features.map((feature, i) => (
            <div key={i} style={{padding:'12px 16px',background:'rgba(139,92,246,0.08)',border:'1px solid rgba(139,92,246,0.15)',borderRadius:'10px',display:'flex',alignItems:'center',gap:'10px'}}>
              <span style={{color:'#10b981',fontSize:'16px'}}>✓</span>
              <span style={{color:'#cbd5e1',fontSize:'14px'}}>{feature}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Usage This Month */}
      <div className="dashboard-card" style={{marginBottom:'24px'}}>
        <h2 style={{color:'#e2e8f0',margin:'0 0 16px',fontSize:'18px'}}>📊 Usage This Month</h2>
        <div style={{display:'flex',gap:'24px',flexWrap:'wrap'}}>
          <div style={{flex:1,minWidth:'150px',padding:'20px',background:'rgba(139,92,246,0.08)',border:'1px solid rgba(139,92,246,0.15)',borderRadius:'12px',textAlign:'center'}}>
            <p style={{color:'#a78bfa',fontSize:'36px',fontWeight:'700',margin:'0 0 4px'}}>{usage}</p>
            <p style={{color:'#94a3b8',fontSize:'13px',margin:0}}>Videos Used</p>
          </div>
          <div style={{flex:1,minWidth:'150px',padding:'20px',background:'rgba(6,182,212,0.08)',border:'1px solid rgba(6,182,212,0.15)',borderRadius:'12px',textAlign:'center'}}>
            <p style={{color:'#06b6d4',fontSize:'36px',fontWeight:'700',margin:'0 0 4px'}}>{limit}</p>
            <p style={{color:'#94a3b8',fontSize:'13px',margin:0}}>Monthly Limit</p>
          </div>
          <div style={{flex:1,minWidth:'150px',padding:'20px',background:remaining<=2?'rgba(239,68,68,0.08)':'rgba(16,185,129,0.08)',border:'1px solid '+(remaining<=2?'rgba(239,68,68,0.15)':'rgba(16,185,129,0.15)'),borderRadius:'12px',textAlign:'center'}}>
            <p style={{color:remaining<=2?'#ef4444':'#10b981',fontSize:'36px',fontWeight:'700',margin:'0 0 4px'}}>{remaining}</p>
            <p style={{color:'#94a3b8',fontSize:'13px',margin:0}}>Remaining</p>
          </div>
        </div>
        <div style={{marginTop:'16px',background:'rgba(255,255,255,0.05)',borderRadius:'8px',height:'8px',overflow:'hidden'}}>
          <div style={{width:usagePercent+'%',height:'100%',background:usagePercent>80?'linear-gradient(90deg,#f59e0b,#ef4444)':'linear-gradient(90deg,#8b5cf6,#06b6d4)',borderRadius:'8px',transition:'width 0.5s'}}></div>
        </div>
        {analytics?.nextResetDate && (
          <p style={{color:'#64748b',fontSize:'12px',marginTop:'8px'}}>Resets on {new Date(analytics.nextResetDate).toLocaleDateString('en-GB',{day:'numeric',month:'short',year:'numeric'})}</p>
        )}
        {(analytics?.referralCredits || 0) > 0 && (
          <div style={{marginTop:'12px',padding:'10px 16px',background:'rgba(251,191,36,0.1)',border:'1px solid rgba(251,191,36,0.2)',borderRadius:'8px'}}>
            <span style={{color:'#fbbf24',fontSize:'13px'}}>🎁 +{analytics.referralCredits} bonus credits from referrals</span>
          </div>
        )}
      </div>

      {/* Payment Method */}
      <div className="dashboard-card" style={{marginBottom:'24px'}}>
        <h2 style={{color:'#e2e8f0',margin:'0 0 16px',fontSize:'18px'}}>💳 Payment Method</h2>
        {userPlan === 'free' || userPlan === 'starter' ? (
          <div style={{padding:'24px',background:'rgba(251,191,36,0.08)',border:'1px solid rgba(251,191,36,0.2)',borderRadius:'12px',textAlign:'center'}}>
            <p style={{color:'#fbbf24',fontSize:'15px',margin:'0 0 12px'}}>No payment method on file</p>
            <p style={{color:'#94a3b8',fontSize:'13px',margin:'0 0 16px'}}>Add a payment method to upgrade your plan and unlock premium features.</p>
            <button onClick={() => onNavigate && onNavigate('upgrade')}
              style={{padding:'12px 24px',borderRadius:'10px',border:'none',background:'linear-gradient(135deg,#8b5cf6,#7c3aed)',color:'white',fontWeight:'600',fontSize:'14px',cursor:'pointer'}}>
              View Plans & Upgrade
            </button>
          </div>
        ) : (
          <div style={{padding:'20px',background:'rgba(139,92,246,0.08)',border:'1px solid rgba(139,92,246,0.15)',borderRadius:'12px'}}>
            <div style={{display:'flex',alignItems:'center',gap:'12px',marginBottom:'12px'}}>
              <span style={{fontSize:'24px'}}>💳</span>
              <div>
                <p style={{color:'#e2e8f0',fontSize:'14px',fontWeight:'600',margin:0}}>Payment via Stripe</p>
                <p style={{color:'#94a3b8',fontSize:'12px',margin:'2px 0 0'}}>Manage your subscription, update payment method, or cancel</p>
              </div>
            </div>
            <button
              onClick={handleManageSubscription}
              disabled={portalLoading}
              style={{padding:'10px 20px',borderRadius:'8px',border:'1px solid rgba(139,92,246,0.3)',background:'rgba(139,92,246,0.1)',color:'#a78bfa',fontWeight:'600',fontSize:'13px',cursor:portalLoading?'wait':'pointer',opacity:portalLoading?0.6:1}}>
              {portalLoading ? 'Opening...' : 'Manage Subscription →'}
            </button>
          </div>
        )}
      </div>

      {/* Billing History */}
      <div className="dashboard-card">
        <h2 style={{color:'#e2e8f0',margin:'0 0 16px',fontSize:'18px'}}>🧾 Billing History</h2>
        {billingLoading ? (
          <div style={{padding:'24px',textAlign:'center'}}>
            <div className="loading-spinner" style={{margin:'0 auto 12px'}}></div>
            <p style={{color:'#64748b',fontSize:'13px',margin:0}}>Loading invoices...</p>
          </div>
        ) : billingHistory.length === 0 ? (
          <div style={{padding:'24px',textAlign:'center',color:'#64748b',fontSize:'14px'}}>
            <p style={{margin:0}}>No billing history yet.</p>
            <p style={{margin:'8px 0 0',fontSize:'12px'}}>Invoices will appear here once you subscribe to a paid plan.</p>
          </div>
        ) : (
          <div style={{overflowX:'auto'}}>
            <table style={{width:'100%',borderCollapse:'collapse'}}>
              <thead>
                <tr style={{borderBottom:'1px solid rgba(255,255,255,0.1)'}}>
                  <th style={{padding:'10px 12px',textAlign:'left',color:'#94a3b8',fontSize:'12px',fontWeight:'600',textTransform:'uppercase',letterSpacing:'0.5px'}}>Date</th>
                  <th style={{padding:'10px 12px',textAlign:'left',color:'#94a3b8',fontSize:'12px',fontWeight:'600',textTransform:'uppercase',letterSpacing:'0.5px'}}>Description</th>
                  <th style={{padding:'10px 12px',textAlign:'right',color:'#94a3b8',fontSize:'12px',fontWeight:'600',textTransform:'uppercase',letterSpacing:'0.5px'}}>Amount</th>
                  <th style={{padding:'10px 12px',textAlign:'center',color:'#94a3b8',fontSize:'12px',fontWeight:'600',textTransform:'uppercase',letterSpacing:'0.5px'}}>Status</th>
                  <th style={{padding:'10px 12px',textAlign:'center',color:'#94a3b8',fontSize:'12px',fontWeight:'600',textTransform:'uppercase',letterSpacing:'0.5px'}}>Invoice</th>
                </tr>
              </thead>
              <tbody>
                {billingHistory.map((inv) => (
                  <tr key={inv.id} style={{borderBottom:'1px solid rgba(255,255,255,0.05)'}}>
                    <td style={{padding:'12px',color:'#cbd5e1',fontSize:'13px',whiteSpace:'nowrap'}}>{formatDate(inv.date)}</td>
                    <td style={{padding:'12px',color:'#e2e8f0',fontSize:'13px'}}>{inv.description}</td>
                    <td style={{padding:'12px',color:'#e2e8f0',fontSize:'14px',fontWeight:'600',textAlign:'right',whiteSpace:'nowrap'}}>
                      {inv.currency === 'GBP' ? '£' : inv.currency === 'USD' ? '$' : inv.currency === 'EUR' ? '€' : ''}{inv.amount}
                    </td>
                    <td style={{padding:'12px',textAlign:'center'}}>{getStatusBadge(inv.status)}</td>
                    <td style={{padding:'12px',textAlign:'center'}}>
                      {inv.invoiceUrl ? (
                        <a href={inv.invoiceUrl} target="_blank" rel="noopener noreferrer"
                          style={{color:'#8b5cf6',textDecoration:'none',fontSize:'12px',fontWeight:'600'}}>
                          View
                        </a>
                      ) : (
                        <span style={{color:'#475569',fontSize:'12px'}}>—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default BillingPanel;
